import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { QuickPriceCalc } from "@/components/QuickPriceCalc";
import {useLang, tr } from "@/i18n";
import { useLiveEvents } from "@/lib/useLiveEvents";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { MobileBottomNav } from "@/components/MobileBottomNav";
import { LayoutDashboard, Calculator, Scale, Undo2, Package, ArrowLeftRight, ScrollText, Truck, Users, BarChart3, LogOut, Wrench, Menu, X, UserCog, Settings as SettingsIcon, ClipboardList, Receipt, ShoppingCart, Bell, Wallet, ScanLine, Banknote, Calendar, LayoutGrid, Monitor, PackageOpen, Building2, Mail, Car, Landmark, Clock, Rocket, Timer, FileText, KeyRound } from "lucide-react";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import InstallAppBanner from "@/components/InstallAppBanner";
import { cn } from "@/lib/utils";
import RunningClocks from "@/components/RunningClocks";
import NotificationBell from "@/components/NotificationBell";
import GlobalSearch from "@/components/GlobalSearch";
import OfflineBanner from "@/components/OfflineBanner";

/* Menu per afdeling. `perm` = zichtbaar met dit recht; `owner` = alleen eigenaar. */
const NAV = [
  // Werkplaats
  { to: "/workboard", key: "workboard", icon: LayoutGrid, testId: "nav-workboard", perm: "calendar.view", group: "navWorkshop" },
  { to: "/repairs", key: "jobCards", icon: ClipboardList, testId: "nav-repairs", perm: "repairs.view", group: "navWorkshop" },
  { to: "/labor-times", key: "laborTimesTitle", icon: Timer, testId: "nav-labor-times", perm: "repairs.view", group: "navWorkshop" },
  { to: "/quotes", key: "quotesTitle", icon: FileText, testId: "nav-quotes", perm: "repairs.view", group: "navReception" },
  { to: "/rentals", key: "rentalsTitle", icon: KeyRound, testId: "nav-rentals", perm: "repairs.view", group: "navReception" },
  { to: "/bay-board", key: "bayBoard", icon: Monitor, testId: "nav-bayboard", perm: "calendar.view", group: "navWorkshop" },
  // Receptie
  { to: "/calendar", key: "calendar", icon: Calendar, testId: "nav-calendar", perm: "calendar.view", group: "navReception" },
  { to: "/customers", key: "customers", icon: Users, testId: "nav-customers", perm: "customers.view", group: "navReception" },
  { to: "/vehicles", key: "vehicles", icon: Car, testId: "nav-vehicles", perm: "customers.view", group: "navReception" },
  { to: "/reminders", key: "reminders", icon: Bell, testId: "nav-reminders", perm: "reminders.view", group: "navReception" },
  { to: "/cash-register", key: "cashRegister", icon: Wallet, testId: "nav-till", perm: "cash.view", group: "navReception" },
  // Magazijn
  { to: "/inventory", key: "inventory", icon: Package, testId: "nav-inventory", perm: "inventory.view", group: "navWarehouse" },
  { to: "/transactions", key: "stockMovements", icon: ArrowLeftRight, testId: "nav-transactions", perm: "inventory.view", group: "navWarehouse" },
  { to: "/delivery-scan", key: "deliveryScan", icon: PackageOpen, testId: "nav-delivery-scan", perm: "delivery_scan.use", group: "navWarehouse" },
  { to: "/sourcing", key: "sourcingTitle", icon: Scale, testId: "nav-sourcing", perm: "prices.inventory", group: "navWarehouse" },
  { to: "/purchase-orders", key: "purchaseOrders", icon: ShoppingCart, testId: "nav-po", perm: "purchase_orders.view", group: "navWarehouse" },
  { to: "/returns", key: "returnsTitle", icon: Undo2, testId: "nav-returns", perm: "purchase_orders.view", group: "navWarehouse" },
  { to: "/suppliers", key: "suppliers", icon: Truck, testId: "nav-suppliers", perm: "suppliers.view", group: "navWarehouse" },
  // Administratie
  { to: "/invoices", key: "invoices", icon: Receipt, testId: "nav-invoices", perm: "invoices.view", group: "navAdmin" },
  { to: "/bank", key: "bank", icon: Landmark, testId: "nav-bank", perm: "bank.view", altPerm: "accounts.view", group: "navAdmin" },
  { to: "/accounting", key: "accounting", icon: Building2, testId: "nav-accounting", owner: true, group: "navAdmin" },
  // Personeel
  { to: "/staff", key: "staff", icon: UserCog, testId: "nav-staff", owner: true, group: "navStaff" },
  { to: "/hours", key: "hoursTitle", icon: Clock, testId: "nav-hours", perm: "hours.view", group: "navStaff" },
  // Directie
  { to: "/dashboard", key: "dashboard", icon: LayoutDashboard, testId: "nav-dashboard", perm: "reports.view", group: "navDirection" },
  { to: "/attention", key: "attentionTitle", icon: Bell, testId: "nav-attention", perm: "reports.view", group: "navDirection" },
  { to: "/pricing", key: "pricingTitle", icon: Calculator, testId: "nav-pricing", perm: "prices.inventory", group: "navDirection" },
  { to: "/reports", key: "reports", icon: BarChart3, testId: "nav-reports", perm: "reports.view", group: "navDirection" },
  { to: "/audit-log", key: "auditLog", icon: ScrollText, testId: "nav-audit", perm: "audit.view", group: "navDirection" },
  { to: "/golive", key: "goliveTitle", icon: Rocket, testId: "nav-golive", owner: true, group: "navDirection" },
  { to: "/settings", key: "settings", icon: SettingsIcon, testId: "nav-settings", owner: true, group: "navDirection" },
];
const NAV_GROUPS = ["navWorkshop", "navReception", "navWarehouse", "navAdmin", "navStaff", "navDirection"];

export default function DashboardLayout({ children }) {
  const { user, logout, hasPermission } = useAuth();
  const { t, meta } = useLang();
  const [open, setOpen] = useState(false);
  const isRTL = meta.dir === "rtl";
  // Staff without a nav item's permission simply don't see it. Owner &
  // super_admin bypass this filter (hasPermission returns true for them).
  const isOwner = user?.role === "owner" || (user?.role === "super_admin" && user?.impersonating);
  const visibleNav = NAV.filter((n) => (n.owner ? isOwner : (!n.perm || hasPermission(n.perm) || (n.altPerm && hasPermission(n.altPerm)))));
  const live = useLiveEvents(!!user);

  return (
    <div className="min-h-screen bg-background flex">
      <InstallAppBanner />
      <OfflineBanner />
      {/* Sidebar */}
      <aside className={cn(
        "gf-sidebar fixed lg:static inset-y-0 z-40 w-64 border-border bg-card flex flex-col transition-transform duration-200",
        isRTL ? "right-0 border-l" : "left-0 border-r",
        open ? "translate-x-0" : (isRTL ? "translate-x-full lg:translate-x-0" : "-translate-x-full lg:translate-x-0")
      )}>
        <div className="h-16 px-6 flex items-center gap-3 border-b border-border">
          <img src="/logo-v12.png" alt="V12 Garage" className="h-10 w-auto" data-testid="sidebar-logo" />
          <div>
            <div className="font-display font-bold tracking-tight text-white">V12 Garage</div>
            <div className="text-[10px] font-mono uppercase tracking-widest" style={{color:"#fb923c"}}>GarageFix</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {NAV_GROUPS.map((g) => {
            const items = visibleNav.filter((n) => n.group === g);
            if (!items.length) return null;
            return (
              <div key={g} data-testid={`nav-group-${g}`}>
                <div className="pt-3 pb-1 px-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground/70">{t(g)}</div>
                {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={n.testId}
              onClick={() => setOpen(false)}
              className={({ isActive }) => cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-200",
                isActive
                  ? "bg-primary/15 text-primary border border-primary/30"
                  : "text-muted-foreground hover:text-foreground hover:bg-accent border border-transparent"
              )}
            >
              <n.icon className="h-4 w-4" />
              <span>{t(n.key)}</span>
            </NavLink>
                ))}
              </div>
            );
          })}
          {user?.role === "super_admin" && (
            <>
              <div className="pt-3 pb-1 px-3 text-[10px] font-mono uppercase tracking-widest text-primary/80">{tr("hc_platform")}</div>
              <NavLink
                to="/super-admin"
                data-testid="nav-super-admin"
                onClick={() => setOpen(false)}
                className={({ isActive }) => cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-200",
                  isActive
                    ? "bg-primary/15 text-primary border border-primary/30"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent border border-transparent"
                )}
              >
                <Building2 className="h-4 w-4" />
                <span>{tr("hc_garages")}</span>
              </NavLink>
            </>
          )}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="px-3 py-2 mb-2">
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-xs font-mono text-muted-foreground uppercase tracking-widest">{user?.role}</div>
          </div>
          <NavLink
            to="/my-profile"
            data-testid="nav-my-profile"
            onClick={() => setOpen(false)}
            className={({ isActive }) => cn(
              "flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors",
              isActive
                ? "bg-primary/15 text-primary"
                : "text-muted-foreground hover:text-foreground hover:bg-accent"
            )}
          >
            <UserCog className="h-4 w-4" /> {t("myProfile")}
          </NavLink>
          <ChangePasswordDialog triggerLabel={t("changePassword")} />
          <Button
            variant="ghost"
            className="w-full justify-start gap-2 text-muted-foreground hover:text-foreground"
            onClick={logout}
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4" /> {t("signOut")}
          </Button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <ImpersonationBanner />
        <header className="gf-topbar sticky top-0 z-20 h-16 border-b border-border flex items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(!open)} data-testid="sidebar-toggle">
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <div className="hidden sm:flex items-center gap-2 text-xs font-mono text-muted-foreground uppercase tracking-widest">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span>{t("live")}</span>
            </div>
            <RunningClocks />
          </div>
          <GlobalSearch />
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="text-xs font-mono text-muted-foreground hidden xl:block">
              {new Date().toLocaleDateString(meta.locale, { weekday: "short", day: "2-digit", month: "short", year: "numeric" })}
            </div>
            <NotificationBell />
            <QuickPriceCalc />
            <span className={`hidden sm:inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest border ${live ? "border-emerald-500/40 text-emerald-600 bg-emerald-500/10" : "border-border text-muted-foreground"}`} title={live ? "Live verbonden: wijzigingen verschijnen direct" : "Polling (15 s)"} data-testid="live-indicator"><span className={`h-1.5 w-1.5 rounded-full ${live ? "bg-emerald-500 animate-pulse" : "bg-muted-foreground"}`} />{live ? "Live" : "Sync"}</span>
            <ThemeToggle />
            <LanguageSwitcher />
            <div className="hidden sm:grid h-8 w-8 place-items-center rounded-full bg-primary/15 text-primary text-xs font-bold border border-primary/30" title={user?.name} data-testid="topbar-avatar">
              {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
            </div>
          </div>
        </header>
        <main className="flex-1 grid-bg pb-24 lg:pb-0">
          <div className="p-3 sm:p-6 lg:p-10 max-w-[1600px] overflow-x-hidden">
            {children}
          </div>
        </main>
        <MobileBottomNav nav={visibleNav} groups={NAV_GROUPS} />
      </div>
    </div>
  );
}
